import React, { useState } from "react";
import { ArrowRight, BookOpen, Sparkles, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function LinenBuyersPlaybookPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/subscribe", { email: email.trim(), role: "buyer", source: "playbook" });
      setSubscribed(true);
      toast.success("You're on the list — chapter one lands in your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="playbook-page">
      <div className="tracking-overline mb-3">Resources · Coming soon</div>
      <h1 className="font-serif text-4xl md:text-5xl text-[#11211A] tracking-tight leading-tight">
        The Linen Buyer's Playbook
      </h1>
      <p className="text-lg text-[#4A5550] mt-6 leading-relaxed max-w-2xl">
        A practical, no-jargon guide for hotel, motel, resort, serviced-apartment and holiday-park operators
        buying linen and textiles at scale. Written by our procurement team and reviewed by industry veterans.
      </p>

      <div className="mt-14 grid md:grid-cols-2 gap-4">
        {[
          { t: "What GSM actually means for guest experience", b: "How to read towel and sheet weights without the marketing spin — and what your housekeepers will actually notice." },
          { t: "Thread count vs. thread quality",              b: "Why a 400 TC sheet from one mill outperforms a 1000 TC sheet from another." },
          { t: "MOQ negotiation for boutique properties",       b: "Getting hotel-grade pricing without a 500-room-per-quarter order." },
          { t: "Reorder cadence & stockroom math",              b: "The 90-day rolling-cover formula most operators wish they'd learned earlier." },
          { t: "Certifications worth paying for",                b: "OEKO-TEX, GOTS, ISO 9001 — decoded, with sample supplier questions." },
          { t: "Colour matching for existing decor",             b: "Standards, Pantone books, and how to future-proof reorders 3 years out." },
        ].map((c) => (
          <div key={c.t} className="border border-[#E8ECE9] p-6" data-testid="playbook-chapter">
            <BookOpen className="w-5 h-5 text-[#B35C4A] mb-3" />
            <div className="font-medium text-lg text-[#11211A] mb-1">{c.t}</div>
            <p className="text-sm text-[#4A5550] leading-relaxed">{c.b}</p>
          </div>
        ))}
      </div>

      <div className="mt-14 border border-[#264839]/20 bg-[#F0EFEA] p-8" data-testid="playbook-signup">
        <div className="flex items-start gap-4 flex-wrap">
          <Sparkles className="w-6 h-6 text-[#B35C4A] shrink-0 mt-1" />
          <div className="flex-1 min-w-[240px]">
            <div className="font-serif text-2xl text-[#11211A]">Get chapter one when it drops.</div>
            <p className="text-sm text-[#4A5550] mt-2 leading-relaxed max-w-lg">
              We're releasing the Playbook chapter-by-chapter throughout 2026. Drop your email and
              we'll send you the first chapter — free — the moment it's published.
            </p>

            {subscribed ? (
              <div
                className="mt-5 flex items-center gap-2 text-[#264839] font-medium"
                data-testid="playbook-signup-success"
              >
                <CheckCircle2 className="w-5 h-5" />
                You're on the list. Watch your inbox.
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="mt-5 flex flex-wrap gap-2"
                data-testid="playbook-signup-form"
              >
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@hotel.com"
                  className="flex-1 min-w-[220px] border border-[#264839]/30 bg-white px-4 py-3 text-sm text-[#11211A] placeholder:text-[#7D8C85] focus:outline-none focus:border-[#264839]"
                  data-testid="playbook-signup-email"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary disabled:opacity-60"
                  data-testid="playbook-signup-submit"
                >
                  {submitting ? "Sending…" : (<>Send me chapter one <ArrowRight className="w-4 h-4" /></>)}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
